import axios from "axios";
import {
    ASSIGN_USERS_TO_WORKFLOW_API_URL,
    UPSERT_TEMPLATE_ENTITY_TYPE_SIGNERS_URL,
    get_request_config
} from "@/utils/system_constants/constant";
import { parse_response_data, set_busy_state } from "./configure_signers_helpers";
import {
    get_role_label,
    get_role_label_lower,
    get_slot_display_name,
    get_slot_initials
} from "./configure_signers_slot_helpers";

function validate_external_slot(slot, role_label) {
    const external_name = String(slot.external_name || "").trim();
    const email = String(slot.email || "").trim();
    const external_validation_checks = [
        [!external_name, `Add a signer name for the external ${role_label} role before continuing.`],
        [!email, `Add an email address for the external ${role_label} role before continuing.`]
    ];
    const failed_check = external_validation_checks.find(function find_failed_check(check) {
        return check[0];
    });
    const validation_message = failed_check ? failed_check[1] : "";
    return validation_message;
}

function validate_internal_slot(slot, role_label) {
    const internal_validation_checks = [
        [!String(slot.selected_user_id || "").trim(), `Select a signer for the ${role_label} role before continuing.`],
        [!String(slot.email || "").trim(), `Add an email address for ${get_slot_display_name(slot)} before continuing.`]
    ];
    const failed_check = internal_validation_checks.find(function find_failed_check(check) {
        return check[0];
    });
    const validation_message = failed_check ? failed_check[1] : "";
    return validation_message;
}

function build_internal_signer_payload(base_payload, slot) {
    const signer_payload = {
        ...base_payload,
        user_id: Number(slot.selected_user_id)
    };
    return signer_payload;
}

function build_external_signer_payload(base_payload, slot) {
    const signer_payload = {
        ...base_payload,
        full_name: String(slot.external_name || "").trim()
    };
    return signer_payload;
}

function build_signer_payload(slot, signing_order) {
    const base_payload = {
        signing_order,
        email: String(slot.email || "").trim()
    };

    if (slot.source_type === "internal") {
        const internal_payload = build_internal_signer_payload(base_payload, slot);
        return internal_payload;
    }

    const external_payload = build_external_signer_payload(base_payload, slot);
    return external_payload;
}

function has_failed_response(parsed_response) {
    const did_fail = parsed_response?.status && parsed_response.status !== "success";
    return did_fail;
}

function build_signer_configuration_payload(vm) {
    const payload = {
        template_document_entity_type_id: vm.template_document_entity_type_id,
        signing_mode: vm.notify_in_order ? "sequential" : "parallel",
        expected_signer_count: vm.signer_slots.length || 1
    };
    return payload;
}

function build_assignment_payload(vm, group) {
    const payload = {
        workflow_assignment_id: vm.workflow_assignment_id,
        entity_profile_id: vm.entity_profile_id,
        step_id: group.step_id,
        signers: group.signers
    };
    return payload;
}

function get_submit_error_message(error) {
    const response_payload = parse_response_data(error?.response?.data);
    const error_message = response_payload?.message || error?.message || "Failed to save signer configuration.";
    return error_message;
}

function build_step_signers(vm, step) {
    const matching_slots = vm.signer_slots.filter(function filter_step_slots(slot) {
        return slot.step_id === step.id;
    });
    const step_signers = matching_slots.map(function map_signer_payload(slot, index) {
        return build_signer_payload(slot, index + 1);
    });
    return step_signers;
}

async function save_signer_configuration(vm) {
    const signer_config_response = await axios.post(
        UPSERT_TEMPLATE_ENTITY_TYPE_SIGNERS_URL,
        build_signer_configuration_payload(vm),
        get_request_config()
    );
    const parsed_config_response = parse_response_data(signer_config_response.data);
    if (has_failed_response(parsed_config_response)) {
        vm.show_error(parsed_config_response?.message || "Failed to save signer configuration.");
        return false;
    }
    const did_save = true;
    return did_save;
}

function build_missing_context_error(vm) {
    const error_message = vm.load_error || "The selected signing request is missing template context.";
    return error_message;
}

function prepare_signer_groups(vm) {
    const blocking_checks = [
        [vm.loading, function handle_loading() { return null; }],
        [!vm.template_context_ready, function handle_missing_context() {
            vm.show_error(build_missing_context_error(vm));
            return null;
        }],
        [!validate_slots(vm), function handle_invalid_slots() { return null; }]
    ];
    const blocking_check = blocking_checks.find(function find_blocking_check(check) {
        return check[0];
    });
    if (blocking_check) {
        const blocking_result = blocking_check[1]();
        return blocking_result;
    }
    const signer_groups = build_signer_payloads_by_step(vm);
    if (!signer_groups.length) {
        vm.show_error("No signers were prepared for this workflow.");
        return null;
    }
    const prepared_groups = signer_groups;
    return prepared_groups;
}

function emit_submit_success(vm) {
    const configured_summary = build_configured_summary(vm);
    vm.$emit("configured", configured_summary);
    vm.show_success("Signer configuration saved successfully.");
}

async function finalize_configuration_submit(vm, signer_groups) {
    vm.saving = true;
    set_busy_state(vm, true);

    try {
        const did_save_configuration = await save_signer_configuration(vm);
        if (!did_save_configuration) {
            return false;
        }
        const did_assign_signers = await assign_signers_to_workflow(vm, signer_groups);
        if (!did_assign_signers) {
            return false;
        }
        emit_submit_success(vm);
        const did_finalize = true;
        return did_finalize;
    } catch (error) {
        const error_message = get_submit_error_message(error);
        vm.show_error(error_message);
        const did_finalize = false;
        return did_finalize;
    } finally {
        vm.saving = false;
        set_busy_state(vm, false);
    }
}

function build_assignment_request(vm, group) {
    const assignment_request = axios.post(
        ASSIGN_USERS_TO_WORKFLOW_API_URL,
        build_assignment_payload(vm, group),
        get_request_config()
    );
    return assignment_request;
}

async function assign_signers_to_workflow(vm, signer_groups) {
    const assignment_responses = await Promise.all(
        signer_groups.map(function post_signer_group(group) {
            return build_assignment_request(vm, group);
        })
    );
    const parsed_responses = assignment_responses.map(function parse_assignment_response(response) {
        return parse_response_data(response.data);
    });
    const failed_response = parsed_responses.find(function find_failed_response(parsed_response) {
        return has_failed_response(parsed_response);
    });
    if (failed_response) {
        vm.show_error(failed_response?.message || "Failed to assign one or more signers.");
        return false;
    }
    const did_assign = true;
    return did_assign;
}

export const build_validation_message = (slot) => {
    if (!slot) {
        return "";
    }
    const role_label = get_role_label_lower(slot);
    if (slot.source_type === "external") {
        const external_validation_message = validate_external_slot(slot, role_label);
        return external_validation_message;
    }
    const internal_validation_message = validate_internal_slot(slot, role_label);
    return internal_validation_message;
};

export const validate_slots = (vm) => {
    const first_invalid_slot = vm.signer_slots.find(function find_invalid_slot(slot) {
        return build_validation_message(slot);
    });
    if (first_invalid_slot) {
        vm.show_error(build_validation_message(first_invalid_slot));
        return false;
    }
    const are_slots_valid = true;
    return are_slots_valid;
};

export const build_signer_payloads_by_step = (vm) => {
    const signer_payloads = vm.workflow_steps.map(function map_step_signers(step) {
        const step_slots = build_step_signers(vm, step);
        const step_signers = {
            step_id: step.id,
            signers: step_slots
        };
        return step_signers;
    }).filter(function filter_empty_entries(entry) {
        return entry.signers.length;
    });
    const filtered_signer_payloads = signer_payloads;
    return filtered_signer_payloads;
};

export const build_configured_summary = (vm) => {
    const workflow = {
        signing_method: vm.signing_method,
        signing_mode: vm.notify_in_order ? "sequential" : "parallel",
        workflow_assignment_id: vm.workflow_assignment_id,
        workflow_id: vm.workflow_id,
        workflow_name: vm.workflow_name,
        signers: vm.signer_slots.map(function map_configured_signer(slot, index) {
            const signer_summary = {
                key: slot.key,
                order: index + 1,
                step_id: slot.step_id,
                role_name: get_role_label(slot),
                source_type: slot.source_type,
                full_name: get_slot_display_name(slot),
                email: slot.email,
                initials: get_slot_initials(slot)
            };
            return signer_summary;
        })
    };
    const configured_summary = workflow;
    return configured_summary;
};

export const submit_configuration = async (vm) => {
    const signer_groups = prepare_signer_groups(vm);
    if (!signer_groups) {
        return false;
    }
    const submit_result = finalize_configuration_submit(vm, signer_groups);
    return submit_result;
};
